import { Select, SelectOption } from '@99designs/common/components/Select';
import { Industry } from '__generated__/Industry';
import Text from 'common/components/Text';
import TextArea from 'common/components/TextArea';
import { handlePromise } from 'common/utils/Bugsnag';
import Link from 'launch/components/Link';
import { ObservableForm, Observe } from 'projects/components/ObservableForm';
import BriefStep from 'projects/components/SmartShoot/BriefStep';
import BriefStepField from 'projects/components/SmartShoot/BriefStepField';
import BriefStepFieldPair from 'projects/components/SmartShoot/BriefStepFieldPair';
import React from 'react';

interface ContactFormFields {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    businessName: string;
    industry: string;
    other: string;
}

interface Props<T> {
    form: ObservableForm<T>;
    step: number;
    industries: Industry[];
    briefType: 'video' | 'photo';
}

const ContactForm: <T extends ContactFormFields>(
    p: Props<T>
) => React.ReactElement<Props<T>> = props => (
    <BriefStep
        step={props.step}
        heading="Your contact information"
        subheading="Tell us a little about yourself"
        briefType={props.briefType}
    >
        <BriefStepFieldPair>
            <Observe
                form={props.form}
                name="firstName"
                render={() => (
                    <BriefStepField
                        label="First name"
                        errorMessage={props.form.getError('firstName')}
                        required
                    >
                        <Text
                            placeholder="Bilbo"
                            maxLength={32}
                            fill
                            {...props.form.attach('firstName')}
                        />
                    </BriefStepField>
                )}
            />
            <Observe
                form={props.form}
                name="lastName"
                render={() => (
                    <BriefStepField
                        label="Last name"
                        errorMessage={props.form.getError('lastName')}
                        required
                    >
                        <Text
                            placeholder="Baggins"
                            maxLength={32}
                            fill
                            {...props.form.attach('lastName')}
                        />
                    </BriefStepField>
                )}
            />
        </BriefStepFieldPair>

        <BriefStepFieldPair>
            <Observe
                form={props.form}
                name="email"
                render={() => (
                    <BriefStepField
                        label="Email"
                        errorMessage={props.form.getError('email')}
                        required
                    >
                        <Text
                            placeholder="bbaggins@theshire.com"
                            maxLength={254}
                            fill
                            {...props.form.attach('email')}
                        />
                    </BriefStepField>
                )}
            />
            <Observe
                form={props.form}
                name="phone"
                render={() => (
                    <BriefStepField
                        label="Phone"
                        errorMessage={props.form.getError('phone')}
                        required
                    >
                        <Text
                            placeholder="1 555 555 1234"
                            maxLength={24}
                            fill
                            {...props.form.attach('phone')}
                        />
                    </BriefStepField>
                )}
            />
        </BriefStepFieldPair>

        <BriefStepFieldPair>
            <Observe
                form={props.form}
                name="businessName"
                render={() => (
                    <BriefStepField
                        label="Business name"
                        errorMessage={props.form.getError('businessName')}
                    >
                        <Text
                            placeholder="Hobbiton Homewares"
                            maxLength={500}
                            fill
                            {...props.form.attach('businessName')}
                        />
                    </BriefStepField>
                )}
            />
            <Observe
                form={props.form}
                name="industry"
                render={() => (
                    <BriefStepField label="Industry">
                        <Select
                            name="industry"
                            options={industryOptions(props.industries)}
                            onChange={({ value }) => {
                                handlePromise(
                                    props.form.set('industry', value),
                                    'VideoBrief Industry'
                                );
                            }}
                            value={props.form.get('industry')}
                        />
                    </BriefStepField>
                )}
            />
        </BriefStepFieldPair>

        <Observe
            form={props.form}
            name="other"
            render={() => (
                <>
                    <BriefStepField
                        label="Anything else you'd like to share with your filmmaker?"
                        errorMessage={props.form.getError('other')}
                    >
                        <TextArea
                            placeholder="Include any other details..."
                            maxLength={500}
                            fill
                            {...props.form.attach('other')}
                        />
                    </BriefStepField>
                    <div className="paragraph paragraph--spacious-bottom paragraph--small paragraph--soft">
                        By completing this form, you agree to our{' '}
                        <Link
                            noWrap={true}
                            href="/legal/privacy"
                            target="_blank"
                            variations={['default']}
                        >
                            Privacy Policy
                        </Link>{' '}
                        and have read and understood the{' '}
                        <Link
                            noWrap={true}
                            href="/legal/smartshoot-terms-of-use"
                            target="_blank"
                            variations={['default']}
                        >
                            Terms of Service
                        </Link>
                    </div>
                </>
            )}
        />
    </BriefStep>
);

function industryOptions(industries: Industry[]): SelectOption[] {
    return [
        { label: 'Select...', value: '', disabled: true },
        ...industries.map(({ key, title }) => ({ label: title, value: key })),
    ];
}

export default ContactForm;
